import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, BookOpen, Search, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Money, FilterSelect, useMeta } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function EmployeeDialog({ open, onOpenChange, employee, onSaved }) {
  const blank = { code: "", name: "", department: "", branch: "", status: "active" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(employee ? { ...blank, ...employee } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, open]);
  const save = async () => {
    setBusy(true);
    try {
      if (employee) await api.put(`/employees/${employee.code}`, { name: f.name, department: f.department, branch: f.branch, status: f.status });
      else await api.post("/employees", f);
      toast.success(employee ? "Employee updated" : "Employee added");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const field = (k, label, props = {}) => (
    <div>
      <Label>{label}</Label>
      <Input className="mt-1.5" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`employee-form-${k}`} {...props} />
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="employee-dialog">
        <DialogHeader>
          <DialogTitle>{employee ? `Edit ${employee.code}` : "Add employee"}</DialogTitle>
          <DialogDescription>{employee ? "Employee code is permanent and cannot be changed. Ledger history is always preserved." : "Employee code is the permanent unique identifier."}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {field("code", "Employee code *", { disabled: !!employee, className: "mt-1.5 money" })}
          {field("name", "Employee name *")}
          {field("department", "Department")}
          {field("branch", "Branch / Location")}
          <div className="sm:col-span-2 flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="text-sm font-medium">Employment status</div>
              <div className="text-xs text-slate-500">Inactive employees keep their full ledger.</div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className={f.status === "active" ? "text-emerald-700 font-medium" : "text-slate-500"}>{f.status === "active" ? "Active" : "Inactive"}</span>
              <Switch checked={f.status === "active"} onCheckedChange={(v) => setF({ ...f, status: v ? "active" : "inactive" })} data-testid="employee-form-status" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} disabled={busy || !f.code.trim() || !f.name.trim()} data-testid="employee-form-save">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Employees() {
  const { can } = useAuth();
  const meta = useMeta();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [department, setDepartment] = useState("");
  const [branch, setBranch] = useState("");
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState({ open: false, employee: null });

  const load = useCallback(() => {
    api.get("/employees", { params: { q, status, department, branch } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, status, department, branch]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Employees"
        subtitle={rows ? `${rows.length} employee${rows.length === 1 ? "" : "s"}` : "Employee master"}
        actions={can("admin") && (
          <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => setDlg({ open: true, employee: null })} data-testid="add-employee-btn">
            <Plus className="h-4 w-4" /> Add employee
          </Button>
        )}
      />
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name or employee code" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 bg-white h-10" data-testid="employee-search-input" />
        </div>
        <FilterSelect value={status} onChange={setStatus} options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} placeholder="All statuses" testid="employee-status-filter" />
        {meta.departments.length > 0 && <FilterSelect value={department} onChange={setDepartment} options={meta.departments} placeholder="All departments" testid="employee-dept-filter" />}
        {meta.branches.length > 0 && <FilterSelect value={branch} onChange={setBranch} options={meta.branches} placeholder="All branches" testid="employee-branch-filter" />}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="employees-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 border-b">
            <tr>
              <th className="px-4 py-2.5 text-left">Code</th><th className="px-4 py-2.5 text-left">Name</th>
              <th className="px-4 py-2.5 text-left hidden md:table-cell">Department</th><th className="px-4 py-2.5 text-left hidden md:table-cell">Branch</th>
              <th className="px-4 py-2.5 text-left">Status</th><th className="px-4 py-2.5 text-right">Outstanding</th><th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={7} className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-500">No employees found. {can("admin") && <Link to="/import" className="text-blue-800 underline">Import from Excel</Link>}</td></tr>}
            {rows?.map((e) => (
              <tr key={e.code} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`employee-row-${e.code}`}>
                <td className="px-4 py-2.5 money text-blue-800 font-semibold">{e.code}</td>
                <td className="px-4 py-2.5 font-medium text-slate-800">{e.name}</td>
                <td className="px-4 py-2.5 hidden md:table-cell text-slate-600">{e.department || "—"}</td>
                <td className="px-4 py-2.5 hidden md:table-cell text-slate-600">{e.branch || "—"}</td>
                <td className="px-4 py-2.5">
                  <span className={`text-xs rounded-full px-2 py-0.5 ${e.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.status === "active" ? "Active" : "Inactive"}</span>
                </td>
                <td className={`px-4 py-2.5 text-right whitespace-nowrap ${e.balance > 0 ? "text-red-700 font-semibold" : "text-slate-500"}`}>
                  {e.has_ledger ? <Money value={e.balance} /> : <span className="text-xs text-slate-400">No ledger</span>}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <Button asChild variant="ghost" size="sm" data-testid={`employee-ledger-btn-${e.code}`}><Link to={`/ledger/${e.code}`}><BookOpen className="h-4 w-4" /><span className="hidden sm:inline">Ledger</span></Link></Button>
                  {can("admin") && (
                    <Button variant="ghost" size="sm" onClick={() => setDlg({ open: true, employee: e })} data-testid={`employee-edit-btn-${e.code}`}><Pencil className="h-4 w-4" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <EmployeeDialog open={dlg.open} employee={dlg.employee} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} onSaved={load} />
    </div>
  );
}
